import { Divider, Stack, Typography } from '@mui/material';
import { useDisclosure } from '@shared/hooks';
import {
  ExportDialog,
  ExportOptions,
  exportSchema,
  ExportSchema,
  ExportTable,
  exportTotalfile,
} from '@retail/export/components';
import { useLanguageSelector } from '@retail/app/hooks';
import { useToast } from '@retail/hooks';
import {
  useFetchExportTotalFileCsv,
  useFetchExportTotalFileZip,
  useFetchTotalFileDownloadStatus,
} from '@retail/export/data-access';
import { TotalFileDownload } from '@retail/export/types';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppTFunction } from '@retail/app/i18n';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getLLDateWithTime } from '@retail/utils';
import { useQueryClient } from '@tanstack/react-query';
import { SearchInputField } from '@retail/components';
import { ExportToolbar } from './ExportToolbar';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';

export const ExportContainer = () => {
  const t = useAppTFunction();

  const toast = useToast();
  const queryClient = useQueryClient();
  const { language } = useLanguageSelector();

  const { assortmentId } = useSelectedOrgUnit();

  const {
    isOpen: isExportDialogOpen,
    onToggle: toggleExportDialog,
    onClose: closeExportDialog,
  } = useDisclosure(false);

  const [selectedSupplierIds, setSelectedSupplierIds] = useState<number[]>([]);
  const [supplierSearch, setSupplierSearch] = useState<string>('');

  const formMethods = useForm<ExportSchema>({
    mode: 'onSubmit',
    resolver: yupResolver(exportSchema(t)),
  });
  const fromDate = formMethods.watch('fromDate');

  const formatDateTime = (date?: Date) =>
    date && dayjs(date).isValid()
      ? dayjs(date.toISOString()).format('YYYY-MM-DDTHH:mm')
      : undefined;

  const { mutateAsync: mutateExportTotalFile, isPending: exportTotalFileIsLoading } =
    useFetchExportTotalFileZip();

  const { mutateAsync: mutateExportTotalFileCsv, isPending: exportTotalFileCsvIsLoading } =
    useFetchExportTotalFileCsv();

  const { data: totalFileSummaryDTO, isLoading: isLoadingSummary } =
    useFetchTotalFileDownloadStatus({
      assortmentId,
      from: formatDateTime(fromDate),
      suspense: false,
    });

  const exportObject = useMemo(
    () => (totalFileSummaryDTO ? TotalFileDownload.fromDto(totalFileSummaryDTO) : undefined),
    [totalFileSummaryDTO]
  );

  const filteredSuppliers = useMemo(
    () =>
      exportObject?.suppliers?.filter(({ mgSupplierName, mgSupplierId, finfoSupplierId }) => {
        return (
          mgSupplierName?.toLocaleLowerCase().startsWith(supplierSearch.toLocaleLowerCase()) ||
          (mgSupplierId + '').toLocaleLowerCase().startsWith(supplierSearch.toLocaleLowerCase()) ||
          (finfoSupplierId + '').toLocaleLowerCase().startsWith(supplierSearch.toLocaleLowerCase())
        );
      }),
    [exportObject?.suppliers, supplierSearch]
  );

  const handleUpdateDateTimePicker = useCallback(
    (fromDate: Date | null) => formMethods.setValue('fromDate', fromDate ?? undefined),
    [formMethods]
  );

  const handleFormSubmit = (data: ExportSchema, csv: boolean) => {
    const from = data?.fromDate?.toISOString() ? formatDateTime(data?.fromDate) : undefined;

    const props = {
      assortmentId,
      supplierIds: selectedSupplierIds,
      from,
      downloadComplete: data.downloadSelection === 'all',
      vilmaVersion: data.vilmaVersion,
      //numberOfFutureDays: data.numberOfFutureDays ? data.numberOfFutureDays : undefined,
    };

    if (csv) {
      mutateExportTotalFileCsv(props)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['totalFileSummary'] });
          toast.success(t('export.messages.fileDownloaded'));
          closeExportDialog();
        })
        .catch((err) => {
          console.error(err);
          toast.error(t('app.error.generic'));
        });
    } else {
      mutateExportTotalFile(props)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['totalFileSummary'] });
          toast.success(t('export.messages.fileDownloaded'));
          closeExportDialog();
        })
        .catch((err) => {
          console.error(err);
          toast.error(t('app.error.generic'));
        });
    }
  };

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSupplierSearch(event.target.value);
  };

  const storedExportChoices = localStorage.getItem(exportTotalfile);
  const exportChoices = (
    storedExportChoices
      ? JSON.parse(storedExportChoices)
      : { fileType: undefined, numberOfFutureDays: undefined }
  ) as ExportOptions;

  useEffect(() => {
    const subscription = formMethods.watch((formValues) => {
      const numberOfFutureDays = Number(formValues.numberOfFutureDays);
      if (
        (!!numberOfFutureDays &&
          numberOfFutureDays >= 0 &&
          numberOfFutureDays <= 21 &&
          formValues.numberOfFutureDays !== exportChoices.futureDays) ||
        (!!formValues.vilmaVersion && formValues.vilmaVersion !== exportChoices.vilmaVersion)
      ) {
        const updatedExportChoices = {
          vilmaVersion: formValues.vilmaVersion,
          futureDays: formValues.numberOfFutureDays,
        };

        localStorage.setItem(exportTotalfile, JSON.stringify(updatedExportChoices));
      }
    });
    return () => subscription.unsubscribe();
  }, [exportChoices.futureDays, exportChoices.vilmaVersion, formMethods]);

  return (
    <Stack flexGrow={1}>
      <FormProvider {...formMethods}>
        <Stack gap={1}>
          <ExportToolbar
            assortmentId={assortmentId}
            handleUpdateDateTimePicker={handleUpdateDateTimePicker}
            toggleExportDialog={toggleExportDialog}
            isLoadingDownload={exportTotalFileIsLoading || exportTotalFileCsvIsLoading}
            disableDownload={!selectedSupplierIds.length}
          />
          <Divider />
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="flex-start"
            px={2}
            pb={1}
          >
            <SearchInputField
              placeholder={t('suppliers.actions.search')}
              value={supplierSearch ?? ''}
              onChange={handleSearchInputChange}
              onReset={() => setSupplierSearch('')}
            />
            <Stack px={2} flexDirection="row" alignItems="center" gap={1}>
              <Typography variant="body2">{t('export.lastRecalculated')}:</Typography>
              <Typography variant="subtitle2">
                {exportObject?.lastRecalculated
                  ? getLLDateWithTime(exportObject.lastRecalculated)
                  : '-'}
              </Typography>
            </Stack>
          </Stack>
        </Stack>

        <ExportTable
          language={language}
          suppliers={filteredSuppliers ?? []}
          isLoading={isLoadingSummary}
          selectedSupplierIds={selectedSupplierIds}
          setSelectedSupplierIds={setSelectedSupplierIds}
        />

        {isExportDialogOpen && (
          <ExportDialog
            isLoading={exportTotalFileIsLoading || exportTotalFileCsvIsLoading}
            defaultExportValues={exportChoices}
            onSubmit={handleFormSubmit}
            onClose={closeExportDialog}
          />
        )}
      </FormProvider>
    </Stack>
  );
};
